import { useState, useEffect } from 'react';
import { ArrowLeft, Check, Loader2, Sparkles, Building2, Users, Target, Star } from 'lucide-react';
import { ProfileLoadingTransition } from '../ProfileLoadingTransition';
import { ClarificationChatbot } from '../ClarificationChatbot';
import {
  generateProfile,
  clarifyProfile,
  confirmProfile,
  escalateProfile,
  BusinessProfile,
  ClarificationThread,
} from '../../../api/business-profile';

interface Step11Props {
  sessionId: string;
  businessName: string;
  onSubmit: () => Promise<void>;
  onBack: () => void;
  saving: boolean;
  submitting: boolean;
}

export function Step11AIProfile({
  sessionId,
  businessName,
  onSubmit,
  onBack,
  saving,
  submitting,
}: Step11Props) {
  // State
  const [profile, setProfile] = useState<BusinessProfile | null>(null);
  const [loading, setLoading] = useState(true);
  const [clarifyLoading, setClarifyLoading] = useState(false);
  const [showChatbot, setShowChatbot] = useState(false);
  const [chatMessages, setChatMessages] = useState<
    ClarificationThread['messages']
  >([]);
  const [clarificationCount, setClarificationCount] = useState(0);
  const [agreed, setAgreed] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isConfirming, setIsConfirming] = useState(false);
  const [escalationMessage, setEscalationMessage] = useState<string | null>(null);

  // Load profile on mount
  useEffect(() => {
    async function loadProfile() {
      try {
        setLoading(true);
        setError(null);

        const result = await generateProfile(sessionId);
        setProfile(result.profile);
        setClarificationCount(result.clarificationCount);

        if (result.clarificationThread) {
          setChatMessages(result.clarificationThread.messages);
        }
      } catch (err) {
        console.error('Failed to generate profile:', err);
        setError(
          err instanceof Error ? err.message : 'Failed to generate profile'
        );
      } finally {
        setLoading(false);
      }
    }

    loadProfile();
  }, [sessionId]);

  // Handle "Not quite..." click
  const handleNotQuite = () => {
    // Add initial assistant message if this is the first clarification
    if (chatMessages.length === 0) {
      setChatMessages([
        {
          role: 'assistant',
          content:
            "Uh oh, no problem! Let's make it niftee! Please tell me in your own words what should be different about your business profile.",
          timestamp: new Date().toISOString(),
        },
      ]);
    }
    setShowChatbot(true);
  };

  // Handle sending a clarification message
  const handleSendMessage = async (message: string) => {
    if (!profile) return;

    // Add customer message to UI immediately
    const customerMessage = {
      role: 'customer' as const,
      content: message,
      timestamp: new Date().toISOString(),
    };
    setChatMessages((prev) => [...prev, customerMessage]);

    try {
      setClarifyLoading(true);

      const result = await clarifyProfile(
        sessionId,
        profile,
        message,
        clarificationCount
      );

      // Update state with response
      setProfile(result.profile);
      setClarificationCount(result.clarificationCount);

      // Add assistant response
      const assistantMessage = {
        role: 'assistant' as const,
        content: result.assistantMessage,
        timestamp: new Date().toISOString(),
        profileSnapshot: result.profile,
      };
      setChatMessages((prev) => [...prev, assistantMessage]);

      // Close chatbot to show updated profile
      setShowChatbot(false);

      // Check if we need to force escalate
      if (result.forceEscalate) {
        handleEscalate('max_rounds');
      }
    } catch (err) {
      console.error('Failed to clarify profile:', err);
      // Add error message
      setChatMessages((prev) => [
        ...prev,
        {
          role: 'assistant',
          content:
            "I had trouble processing that. Could you try explaining in a different way?",
          timestamp: new Date().toISOString(),
        },
      ]);
    } finally {
      setClarifyLoading(false);
    }
  };

  // Handle escalation to human
  const handleEscalate = async (reason: 'customer_requested' | 'max_rounds') => {
    if (!profile) return;

    try {
      const thread: ClarificationThread = {
        messages: chatMessages,
        escalatedAt: new Date().toISOString(),
        escalationReason: reason,
      };

      const result = await escalateProfile(sessionId, profile, thread, reason);
      setEscalationMessage(result.message);
      setShowChatbot(false);
    } catch (err) {
      console.error('Failed to escalate:', err);
    }
  };

  // Handle "Nailed it!" click
  const handleNailedIt = async () => {
    if (!profile || !agreed) return;

    try {
      setIsConfirming(true);

      // Confirm the profile
      await confirmProfile(sessionId, profile);

      // Submit onboarding
      await onSubmit();
    } catch (err) {
      console.error('Failed to submit:', err);
      setError(err instanceof Error ? err.message : 'Failed to submit');
      setIsConfirming(false);
    }
  };

  // Show loading transition
  if (loading) {
    return (
      <ProfileLoadingTransition isVisible={true} businessName={businessName} />
    );
  }

  // Show error state
  if (error && !profile) {
    return (
      <div className="space-y-8">
        <div className="text-center">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">
            Oops, something went wrong
          </h1>
          <p className="text-gray-600 mb-6">{error}</p>
          <button
            onClick={() => window.location.reload()}
            className="px-6 py-3 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition"
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  // Show escalation confirmation
  if (escalationMessage) {
    return (
      <div className="space-y-8">
        <div className="text-center">
          <div className="w-16 h-16 mx-auto bg-green-100 rounded-full flex items-center justify-center mb-4">
            <Check className="w-8 h-8 text-green-600" />
          </div>
          <h1 className="text-3xl font-bold text-gray-900 mb-2">
            Help is on the way!
          </h1>
          <p className="text-gray-600 mb-6">{escalationMessage}</p>
          <p className="text-sm text-gray-500">
            You can save your progress and come back anytime, or continue with
            the current profile.
          </p>
        </div>
        <div className="flex justify-center gap-4">
          <button
            onClick={() => setEscalationMessage(null)}
            className="px-6 py-3 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition"
          >
            Continue with Current Profile
          </button>
        </div>
      </div>
    );
  }

  if (!profile) return null;

  return (
    <>
      <div className="space-y-8">
        {/* Header */}
        <div className="text-center">
          <div className="flex items-center justify-center gap-2 mb-4">
            <Sparkles className="w-6 h-6 text-primary-500" />
            <span className="text-sm font-medium text-primary-600 bg-primary-50 px-3 py-1 rounded-full">
              Almost there!
            </span>
          </div>
          <h1 className="text-3xl font-bold text-gray-900 mb-2">
            Your Brand Profile
          </h1>
          <p className="text-gray-600">
            Here&apos;s what we think we know about your brand so we can build
            you a niftee website to help you grow your business. What do you
            think? Did we nail it?
          </p>
        </div>

        {/* Profile Card */}
        <div className="bg-white border border-gray-200 rounded-2xl shadow-sm overflow-hidden">
          {/* Profile Header */}
          <div className="bg-gradient-to-r from-primary-500 to-primary-600 text-white p-6">
            <h2 className="text-xl font-bold">{businessName}</h2>
            <p className="text-primary-100 text-sm mt-1">
              {profile.industry.specific || profile.industry.primary}
            </p>
          </div>

          {/* Profile Content */}
          <div className="p-6 space-y-6">
            {/* Core Summary */}
            <div>
              <div className="flex items-center gap-2 text-gray-500 text-sm font-medium mb-2">
                <Building2 className="w-4 h-4" />
                What You Do
              </div>
              <p className="text-gray-800">{profile.coreSummary}</p>
            </div>

            {/* Two Column Grid */}
            <div className="grid md:grid-cols-2 gap-6">
              {/* Atmosphere */}
              <div>
                <div className="flex items-center gap-2 text-gray-500 text-sm font-medium mb-2">
                  <Sparkles className="w-4 h-4" />
                  Your Vibe
                </div>
                <p className="text-gray-800">{profile.atmosphere}</p>
              </div>

              {/* Tone */}
              <div>
                <div className="flex items-center gap-2 text-gray-500 text-sm font-medium mb-2">
                  <Star className="w-4 h-4" />
                  Communication Style
                </div>
                <p className="text-gray-800 capitalize">{profile.tone}</p>
              </div>
            </div>

            {/* Ideal Client */}
            <div>
              <div className="flex items-center gap-2 text-gray-500 text-sm font-medium mb-2">
                <Users className="w-4 h-4" />
                Your Ideal Client
              </div>
              <p className="text-gray-800">{profile.idealClient}</p>
            </div>

            {/* What Sets You Apart */}
            <div>
              <div className="flex items-center gap-2 text-gray-500 text-sm font-medium mb-2">
                <Target className="w-4 h-4" />
                What Sets You Apart
              </div>
              <p className="text-gray-800">{profile.uniqueValue}</p>
            </div>

            {/* Services Tags */}
            {profile.services && profile.services.length > 0 && (
              <div>
                <div className="text-gray-500 text-sm font-medium mb-2">
                  Key Services
                </div>
                <div className="flex flex-wrap gap-2">
                  {profile.services.map((service, index) => (
                    <span
                      key={index}
                      className="bg-gray-100 text-gray-700 px-3 py-1 rounded-full text-sm"
                    >
                      {service}
                    </span>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Clarification count indicator */}
        {clarificationCount > 0 && (
          <p className="text-center text-sm text-gray-500">
            Profile updated {clarificationCount} time
            {clarificationCount !== 1 ? 's' : ''} based on your feedback
          </p>
        )}

        {/* Action Buttons */}
        <div className="flex flex-col sm:flex-row gap-3 justify-center">
          <button
            onClick={handleNotQuite}
            className="px-6 py-3 rounded-xl font-medium text-gray-600 bg-gray-100 hover:bg-gray-200 transition-all order-2 sm:order-1"
          >
            Not quite...
          </button>
          <button
            onClick={() => setAgreed(true)}
            disabled={agreed}
            className={`px-8 py-3 rounded-xl font-medium transition-all order-1 sm:order-2 ${
              agreed
                ? 'bg-green-100 text-green-700 cursor-default'
                : 'bg-primary-600 text-white hover:bg-primary-700'
            }`}
          >
            {agreed ? (
              <span className="flex items-center gap-2">
                <Check className="w-5 h-5" />
                Nailed it!
              </span>
            ) : (
              "Nailed it! Let's build my site!"
            )}
          </button>
        </div>

        {/* Confirmation Section (shows after clicking Nailed it) */}
        {agreed && (
          <div className="bg-green-50 border border-green-200 rounded-xl p-6 animate-fade-in">
            <label className="flex items-start gap-3 cursor-pointer">
              <input
                type="checkbox"
                checked={true}
                readOnly
                className="w-5 h-5 mt-0.5 text-green-600 rounded"
              />
              <span className="text-sm text-gray-700">
                I confirm that this profile accurately represents my business
                and I&apos;m ready to have my website built. I understand the
                niftee team will use this information to create my site.
              </span>
            </label>

            <div className="mt-6 flex justify-center">
              <button
                onClick={handleNailedIt}
                disabled={isConfirming || submitting || saving}
                className={`flex items-center gap-2 px-8 py-3 rounded-xl font-medium transition-all ${
                  isConfirming || submitting
                    ? 'bg-gray-200 text-gray-400 cursor-not-allowed'
                    : 'bg-green-600 text-white hover:bg-green-700'
                }`}
              >
                {isConfirming || submitting ? (
                  <>
                    <Loader2 className="w-5 h-5 animate-spin" />
                    Building your site...
                  </>
                ) : (
                  <>
                    <Check className="w-5 h-5" />
                    Submit & Build My Site
                  </>
                )}
              </button>
            </div>
          </div>
        )}

        {/* Back Button */}
        <div className="flex justify-start pt-4">
          <button
            onClick={onBack}
            className="flex items-center gap-2 px-6 py-3 rounded-lg font-medium text-gray-600 hover:bg-gray-100 transition-all"
          >
            <ArrowLeft className="w-4 h-4" />
            Back
          </button>
        </div>
      </div>

      {/* Clarification Chatbot */}
      <ClarificationChatbot
        isOpen={showChatbot}
        messages={chatMessages}
        clarificationCount={clarificationCount}
        onSendMessage={handleSendMessage}
        onRequestHuman={() => handleEscalate('customer_requested')}
        onClose={() => setShowChatbot(false)}
        loading={clarifyLoading}
      />
    </>
  );
}
